import React, { useContext } from 'react';

import { styled } from '@mui/material';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import Users from 'components/SvgComponents/icons/Users';
import { ProjectContext } from 'containers/GlobalWrapper/ProjectDataValidation';
import { selectSpecialRights } from 'containers/GlobalWrapper/selectors';
import { useFeatureFlags } from 'contexts/FeatureFlagsProvider';
import useCurrentUser from 'hooks/useCurrentUser';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import { useFetchKolimmoUserStatusQuery } from 'redux/api/automations.api';
import { useFetchKolimmoAutologinMutation } from 'redux/api/kolimmo.api';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';
import { authorizations } from 'utils/constants';

const KolimmoButtonStyled = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(3),
}));

function KolimmoButton() {
  const intl = useIntl();
  const { organizationId, idData: projectId } = useParams();
  const project = useContext(ProjectContext);
  const featureFlags = useFeatureFlags();
  const specialRights = useSelector(selectSpecialRights());
  const { userId } = useCurrentUser();
  const [fetchKolimmoAutologin] = useFetchKolimmoAutologinMutation();

  // #region [redux]
  const dispatchGlobalError = useGlobalErrorHandler();
  // #endregion

  const isRsoProject = !!(featureFlags?.osr_package && project?.is_occupied_site_renovation);

  // 404 means the user is not set up for Kolimmo, so it's treated as "not enabled" rather than an error.
  const { isSuccess: isKolimmoUser } = useFetchKolimmoUserStatusQuery(
    { organizationId, userId },
    { skip: !isRsoProject || !organizationId || !userId },
  );

  const isAdminOrEditor =
    project?.authorizations?.includes(authorizations.project.delete) ||
    project?.authorizations?.includes(authorizations.project.readSettings);

  const isSuperAdmin = specialRights?.some(
    right => right.action === authorizations.specialUser.readAdminPortal,
  );

  const shouldDisplay =
    isRsoProject && (isAdminOrEditor || isSuperAdmin) && projectId && isKolimmoUser;

  const handleClick = async () => {
    Analytics.track('click_kolimmo_redirection', { project_id: project?.project_id });

    if (!organizationId || !userId) return;

    try {
      const response = await fetchKolimmoAutologin({ organizationId, userId }).unwrap();

      const redirectUrl = response?.presigned_url;

      if (redirectUrl) {
        window.open(redirectUrl, '_blank');
      }
    } catch (error) {
      dispatchGlobalError({
        error,
        defaultErrorMessageIntl: globalMessages.error_refresh_contact_support,
        codeToMessageIntl: {
          AUTOLOGIN_FAILED: globalMessages.no_right_access_tenants_module,
        },
      });
    }
  };

  return shouldDisplay ? (
    <KolimmoButtonStyled
      onClick={handleClick}
      title={intl.formatMessage(globalMessages.open_tenants_module)}
      startIcon={<Users />}
    >
      {intl.formatMessage(globalMessages.open_tenants_module)}
    </KolimmoButtonStyled>
  ) : null;
}

KolimmoButton.propTypes = {};

export default KolimmoButton;
